// passkeyService.ts
// ───────────────────────────────────────────────────────────────────────────
// Cliente del frontend para los 5 endpoints de passkeys.
// Usa @simplewebauthn/browser para la ceremonia local (Touch ID, Face ID,
// Hello, huella) y fetch a las edge functions de Supabase.
// ───────────────────────────────────────────────────────────────────────────

import {
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

import { supabase } from '@/lib/supabase';

const FN_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

async function callEdge<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('No hay sesión activa');
  const r = await fetch(`${FN_BASE}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    let detail: string;
    try {
      const j = (await r.json()) as { error?: string; detail?: string };
      detail = j.detail || j.error || '';
    } catch {
      detail = await r.text();
    }
    throw new Error(`${path} → HTTP ${r.status} · ${detail}`);
  }
  return (await r.json()) as TRes;
}

export type Passkey = {
  id: string;
  friendly_name: string | null;
  device_type: string | null;
  backed_up: boolean;
  transports: string[] | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

/**
 * Registra una passkey nueva.
 *
 * `attachment: 'platform'` pide el autenticador del propio dispositivo (Touch
 * ID, Windows Hello, Google Password Manager, huella): el navegador no ofrece
 * "escanear con el celular". Es lo que usa la entrada rápida, porque cada
 * navegador (y cada PWA de Chrome) guarda sus passkeys por su cuenta. Sin
 * valor, el navegador elige.
 */
export async function enrollPasskey(
  friendly_name: string,
  opts: { attachment?: 'platform' | 'cross-platform' } = {},
): Promise<{
  ok: boolean;
  passkey: { id: string; friendly_name: string; created_at: string };
}> {
  const { options } = await callEdge<
    { attachment?: 'platform' | 'cross-platform' },
    { options: PublicKeyCredentialCreationOptionsJSON }
  >('core-passkey-register-options', { attachment: opts.attachment });

  // El browser dispara el prompt biométrico nativo acá
  const credential = await startRegistration({ optionsJSON: options });

  return await callEdge<
    { credential: typeof credential; friendly_name: string },
    {
      ok: boolean;
      passkey: { id: string; friendly_name: string; created_at: string };
    }
  >('core-passkey-register-verify', { credential, friendly_name });
}

export async function authPasskey(): Promise<{
  ok: boolean;
  refresh_session: boolean;
}> {
  const { options } = await callEdge<
    Record<string, never>,
    { options: PublicKeyCredentialRequestOptionsJSON }
  >('core-passkey-auth-options', {});

  const credential = await startAuthentication({ optionsJSON: options });

  const result = await callEdge<
    { credential: typeof credential },
    { ok: boolean; refresh_session: boolean }
  >('core-passkey-auth-verify', { credential });

  if (result.refresh_session) {
    // Refresh para que el hook re-corra y agregue claim aal_passkey
    await supabase.auth.refreshSession();
  }
  return result;
}

/**
 * Pide una huella fresca SIN iniciar nada: devuelve la credencial firmada para
 * que otra edge (por ejemplo core-delete-user) la verifique ella misma. Sirve
 * para confirmar una acción destructiva sin pasar por la verificación de sesión.
 */
export async function pedirCredencialPasskey() {
  const { options } = await callEdge<
    Record<string, never>,
    { options: PublicKeyCredentialRequestOptionsJSON }
  >('core-passkey-auth-options', {});
  return await startAuthentication({ optionsJSON: options });
}

export async function revokePasskey(passkey_id: string): Promise<void> {
  await callEdge<{ passkey_id: string }, { ok: boolean }>(
    'core-passkey-revoke',
    { passkey_id },
  );
}

export async function listMyPasskeys(): Promise<Passkey[]> {
  // RLS policy own_or_admin_select: dueño ve las suyas
  const { data, error } = await supabase
    .from('user_passkeys')
    .select(
      'id, friendly_name, device_type, backed_up, transports, created_at, last_used_at, revoked_at',
    )
    .is('revoked_at', null)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Passkey[];
}

export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'PublicKeyCredential' in window &&
    typeof window.PublicKeyCredential === 'function'
  );
}

/**
 * ¿Este dispositivo tiene un autenticador propio con verificación de usuario
 * (huella, Face ID, Windows Hello, PIN)? Sin eso no tiene sentido ofrecer la
 * entrada rápida: el alta terminaría pidiendo escanear con otro dispositivo.
 */
export async function hayAutenticadorDelDispositivo(): Promise<boolean> {
  if (!isPasskeySupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}
